import { useCallback, useEffect, useState } from 'react';
import { useTemuShopOptional } from '@/temu-platform/context/TemuShopContext';
import { temuPlatformAPI } from '@/temu-platform/api/temuPlatformApi';
import { showToast } from '@/utils/toast';
import { beginTemuManualSyncClick } from '@/utils/temuManualSyncClick';
import { toTemuManualSyncQuotaKind } from '@/utils/temuManualSyncQuotaKind';

const EMPTY = {
  used: 0,
  remaining: null,
  limit: 10,
  exhausted: false,
  loading: false,
};

/**
 * 当前店铺某个「同步更新」按钮的每日剩余次数。
 * 按店铺 × 按钮分别计次；一次点击成功只扣 1 次，店铺之间互不占用。
 */
export function useTemuManualSyncQuota(kind) {
  const shop = useTemuShopOptional();
  const shopId = shop?.activeShopId || shop?.activeShop?.id || '';
  const buttonKind = toTemuManualSyncQuotaKind(kind);
  const [quota, setQuota] = useState(EMPTY);

  const reload = useCallback(async () => {
    if (!shopId || !buttonKind) {
      setQuota(EMPTY);
      return EMPTY;
    }
    try {
      const res = await temuPlatformAPI.getManualSyncQuota(shopId, buttonKind);
      const limit = Math.max(1, Number(res?.limit) || 10);
      const used = Math.max(0, Number(res?.used) || 0);
      const remaining = Math.max(0, res?.remaining != null ? Number(res.remaining) : limit - used);
      const next = {
        used,
        remaining,
        limit,
        exhausted: remaining <= 0,
        loading: false,
      };
      setQuota(next);
      return next;
    } catch {
      setQuota((prev) => ({ ...prev, loading: false }));
      return null;
    }
  }, [shopId, buttonKind]);

  useEffect(() => {
    setQuota((prev) => ({ ...prev, loading: true }));
    reload();
  }, [reload]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onChange = (ev) => {
      const eventKind = toTemuManualSyncQuotaKind(ev?.detail?.kind);
      const eventShopId = ev?.detail?.shopId != null ? String(ev.detail.shopId) : '';
      if (eventShopId && shopId && eventShopId !== String(shopId)) return;
      if (eventKind && buttonKind && eventKind !== buttonKind) return;
      reload();
    };
    window.addEventListener('ipe:temu-manual-sync-quota', onChange);
    return () => window.removeEventListener('ipe:temu-manual-sync-quota', onChange);
  }, [reload, buttonKind, shopId]);

  const assertCanSync = useCallback(() => {
    if (quota.exhausted) {
      showToast(`该同步今日已达上限（${quota.limit}次），请明天再试`, 'warning');
      return false;
    }
    beginTemuManualSyncClick(buttonKind);
    return true;
  }, [quota.exhausted, quota.limit, buttonKind]);

  return {
    ...quota,
    reload,
    assertCanSync,
  };
}
